import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  ClientProps,
  PumpEntryProps,
  PumpEntryStatus,
  PumpProps,
  WellProps,
} from "../../../interfaces/interfaces";
import { useRequest } from "../../../hooks/useRequest";

const STATUS_OPTIONS: { value: PumpEntryStatus; label: string }[] = [
  { value: "in-stock", label: "In stock" },
  { value: "reused", label: "Reused" },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  apiPrefix: string;
  clientOptions: ClientProps[];
  wellOptions: WellProps[];
  fetchDataWells: (clientId: number) => void;
  initialClientId?: number | null;
  lockedClientId?: number | null;
  editingRow?: PumpEntryProps | null;
}

export const PumpEntryForm = ({
  open,
  onClose,
  onSaved,
  apiPrefix,
  clientOptions,
  wellOptions,
  fetchDataWells,
  initialClientId = null,
  lockedClientId = null,
  editingRow = null,
}: Props) => {
  const { handleRequest } = useRequest();
  const isEdit = Boolean(editingRow);
  const [clientId, setClientId] = useState<number | null>(initialClientId);
  const [pumps, setPumps] = useState<PumpProps[]>([]);
  const [pumpId, setPumpId] = useState<number | "">("");
  const [creatingNewPump, setCreatingNewPump] = useState(false);
  const [newPumpNumber, setNewPumpNumber] = useState("");
  const [newPumpModel, setNewPumpModel] = useState("");
  const [newPumpLocation, setNewPumpLocation] = useState("");
  const [wellId, setWellId] = useState<number | "">("");
  const [pulledDate, setPulledDate] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [status, setStatus] = useState<PumpEntryStatus>("in-stock");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    const startingClientId = editingRow?.clientId ?? lockedClientId ?? initialClientId;
    setClientId(startingClientId);
    setPumps([]);
    setPumpId(editingRow?.pumpId ?? "");
    setCreatingNewPump(false);
    setNewPumpNumber("");
    setNewPumpModel("");
    setNewPumpLocation("");
    setWellId(editingRow?.wellId ?? "");
    setPulledDate(editingRow?.pulledDate?.slice(0, 10) ?? "");
    setQuantity(editingRow?.quantity ?? 1);
    setStatus(editingRow?.status ?? "in-stock");
    setNotes(editingRow?.notes ?? "");
    setError("");
  };

  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingRow]);

  useEffect(() => {
    if (!clientId) {
      setPumps([]);
      return;
    }
    let active = true;
    fetchDataWells(clientId);
    handleRequest({
      endpoint: `${apiPrefix}-pumps/client/${clientId}`,
      options: { method: "GET" },
    })
      .then((response) => {
        if (active) setPumps(response.data || []);
      })
      .catch(() => {
        if (active) setPumps([]);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, apiPrefix]);

  const canSubmit =
    Boolean(clientId) &&
    (creatingNewPump ? Boolean(newPumpNumber.trim()) : Boolean(pumpId)) &&
    Boolean(pulledDate) &&
    quantity > 0;

  const submit = async () => {
    if (!canSubmit || !clientId) return;
    setSaving(true);
    setError("");
    try {
      let resolvedPumpId = pumpId ? Number(pumpId) : null;
      if (creatingNewPump) {
        const pumpResponse = await handleRequest({
          endpoint: `${apiPrefix}-pumps`,
          options: {
            method: "POST",
            body: JSON.stringify({
              clientId,
              pumpNumber: newPumpNumber.trim(),
              model: newPumpModel.trim() || undefined,
              location: newPumpLocation.trim() || undefined,
              wellId: wellId || undefined,
            }),
          },
        });
        if (!pumpResponse.success) {
          throw new Error(pumpResponse.error || "Unable to create pump");
        }
        resolvedPumpId = pumpResponse.data.id;
      }

      const payload = {
        pumpId: resolvedPumpId,
        wellId: wellId || undefined,
        pulledDate,
        quantity,
        notes: notes.trim() || undefined,
        ...(isEdit ? { status } : {}),
      };

      const saveResponse = await handleRequest({
        endpoint: isEdit ? `${apiPrefix}/${editingRow!.id}` : apiPrefix,
        options: {
          method: isEdit ? "PUT" : "POST",
          body: JSON.stringify(isEdit ? payload : { clientId, ...payload }),
        },
      });
      if (!saveResponse.success) {
        throw new Error(saveResponse.error || "Unable to save the pump record");
      }
      onSaved();
      onClose();
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Unable to save the pump record",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 850 }}>
        {isEdit ? "Edit pump record" : "Log pulled pump"}
      </DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Autocomplete
          size="small"
          options={clientOptions}
          value={clientOptions.find((client) => client.id === clientId) || null}
          getOptionLabel={(client) => client.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={Boolean(lockedClientId) || isEdit}
          onChange={(_, client) => {
            setClientId(client?.id || null);
            setPumpId("");
            setCreatingNewPump(false);
            setWellId("");
          }}
          renderInput={(params) => (
            <TextField {...params} label="Client" margin="normal" required />
          )}
        />

        {!creatingNewPump ? (
          <>
            <Autocomplete
              size="small"
              options={pumps}
              value={pumps.find((pump) => pump.id === pumpId) || null}
              getOptionLabel={(pump) => pump.pumpNumber}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={!clientId}
              onChange={(_, pump) => setPumpId(pump?.id || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pump"
                  margin="normal"
                  required
                  placeholder={clientId ? "Select a pump" : "Select a client first"}
                />
              )}
            />
            <Button
              size="small"
              disabled={!clientId}
              onClick={() => {
                setCreatingNewPump(true);
                setPumpId("");
              }}
              sx={{ mt: -1, mb: 1, textTransform: "none", fontWeight: 700 }}
            >
              + Register a new pump
            </Button>
          </>
        ) : (
          <Box sx={{ border: "1px dashed #D8D6CE", borderRadius: 2, p: 2, my: 2 }}>
            <Typography variant="caption" fontWeight={800} color="text.secondary">
              NEW PUMP
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Pump number"
              margin="dense"
              required
              value={newPumpNumber}
              onChange={(event) => setNewPumpNumber(event.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Model"
              margin="dense"
              value={newPumpModel}
              onChange={(event) => setNewPumpModel(event.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Location"
              margin="dense"
              value={newPumpLocation}
              onChange={(event) => setNewPumpLocation(event.target.value)}
            />
            <Button
              size="small"
              onClick={() => setCreatingNewPump(false)}
              sx={{ mt: 0.5, textTransform: "none", fontWeight: 700 }}
            >
              Use an existing pump instead
            </Button>
          </Box>
        )}

        <Autocomplete
          size="small"
          options={wellOptions}
          value={wellOptions.find((well) => well.id === wellId) || null}
          getOptionLabel={(well) => well.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={!clientId}
          onChange={(_, well) => setWellId(well?.id || "")}
          renderInput={(params) => (
            <TextField {...params} label="Well (optional)" margin="normal" />
          )}
        />

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Pull date"
            margin="normal"
            required
            InputLabelProps={{ shrink: true }}
            value={pulledDate}
            onChange={(event) => setPulledDate(event.target.value)}
          />
          <TextField
            size="small"
            type="number"
            label="Qty"
            margin="normal"
            sx={{ width: 110 }}
            inputProps={{ min: 1 }}
            value={quantity}
            onChange={(event) => setQuantity(Math.max(1, Number(event.target.value) || 1))}
          />
        </Box>

        {isEdit ? (
          <FormControl fullWidth size="small" margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(event) => setStatus(event.target.value as PumpEntryStatus)}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        <TextField
          fullWidth
          size="small"
          label="Notes (optional)"
          margin="normal"
          multiline
          minRows={2}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSubmit || saving} onClick={submit}>
          {isEdit ? "Save changes" : "Log pulled pump"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
